import React, { useState } from 'react';
import CollectionsTab from './CollectionsTab';
import { formatCurrency } from '../utils/currency';

export default function InvoiceDetails({
  selectedInvoiceForDetails,
  isLoadingDetails,
  closeDetailsModal,
  detailsTab,
  setDetailsTab,
  clients,
  cases,
  openRecordPaymentModal,
  openWriteOffModal,
  openCreditNoteModal,
  openRefundModal,
  openEditInvoiceModal,
  authAxios,
  showToast,
  setSelectedInvoiceForDetails,
}) {
  const [printingJournalId, setPrintingJournalId] = useState(null);

  const getTaxTypeLabel = (taxType) => {
    const t = String(taxType || 'STANDARD');
    if (t === 'DISBURSEMENT_K') return 'Group K (VAT excluded)';
    return 'Standard (VAT)';
  };

  const getTaxTypeBadgeClass = (taxType) => {
    const t = String(taxType || 'STANDARD');
    if (t === 'DISBURSEMENT_K') return 'bg-amber-50 text-amber-800 ring-amber-200';
    return 'bg-blue-50 text-blue-700 ring-blue-200';
  };

  const getJournalBadgeClass = (journalType) => {
    const t = String(journalType || '');
    if (t.startsWith('PAYMENT_')) return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    if (t === 'REFUND') return 'bg-rose-50 text-rose-700 ring-rose-200';
    if (t === 'WRITE_OFF') return 'bg-amber-50 text-amber-800 ring-amber-200';
    if (t === 'CREDIT_NOTE') return 'bg-blue-50 text-blue-700 ring-blue-200';
    if (t === 'ADJUSTMENT') return 'bg-slate-50 text-slate-700 ring-slate-200';
    return 'bg-gray-50 text-gray-700 ring-gray-200';
  };

  if (!selectedInvoiceForDetails) return null;

  const handleDownloadJournalReceipt = async (journal) => {
    if (!journal?.id) return;
    if (!authAxios) return;
    setPrintingJournalId(journal.id);
    try {
      const response = await authAxios.get(`invoice-journals/${journal.id}/generate_receipt/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_journal_${journal.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast && showToast('Receipt downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error generating journal receipt:', error.response ? error.response.data : error);
      showToast && showToast('Failed to generate receipt for this payment.', 'danger');
    } finally {
      setPrintingJournalId(null);
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailsModal}></div>
        <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-2xl">
            <div className="h-full flex flex-col bg-white shadow-xl">
              <div className="px-4 py-6 sm:px-6 bg-white border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Invoice #{selectedInvoiceForDetails.invoice_number}</h2>
                    <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset bg-gray-50 text-gray-700 ring-gray-200">
                        {selectedInvoiceForDetails.status}
                      </span>
                      <span>Issued {selectedInvoiceForDetails.issue_date}</span>
                      <span className="text-gray-300">•</span>
                      <span>Due {selectedInvoiceForDetails.due_date}</span>
                    </div>
                  </div>
                  <div className="ml-3 h-7 flex items-center">
                    <button onClick={closeDetailsModal} className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <span className="sr-only">Close panel</span>
                      ×
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {isLoadingDetails ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-3 mb-4 rounded-lg border border-gray-200 bg-white p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-600">Client</p>
                          <p className="font-semibold text-gray-900">
                            {selectedInvoiceForDetails.client_full_name || (() => { const c = clients.find(c => c.id === selectedInvoiceForDetails.client); return c ? (c.first_name ? `${c.first_name} ${c.last_name || ''}`.trim() : c.organization_name) : 'N/A'; })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Case</p>
                          <p className="font-semibold text-gray-900">{selectedInvoiceForDetails.case_title || cases.find(c => c.id === selectedInvoiceForDetails.case)?.title || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Subtotal</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.subtotal || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Tax</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.tax_amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Total</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.total_amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200">
                          <p className="text-xs text-emerald-800">Balance</p>
                          <p className="font-semibold text-emerald-900">{formatCurrency(selectedInvoiceForDetails.balance_due || 0)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button onClick={() => setDetailsTab('items')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${detailsTab === 'items' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Items</button>
                        <button onClick={() => setDetailsTab('journals')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${detailsTab === 'journals' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Journals</button>
                        <button onClick={() => setDetailsTab('collections')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${detailsTab === 'collections' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Collections</button>
                      </nav>
                    </div>

                    {detailsTab === 'items' && (
                      <div className="mt-4">
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tax Type</th>
                                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Qty</th>
                                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Unit Price</th>
                                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {(selectedInvoiceForDetails.invoice_items || []).map((item, index) => (
                                  <tr key={item.id || index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-800">{item.description}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getTaxTypeBadgeClass(item.tax_type)}`}>
                                        {getTaxTypeLabel(item.tax_type)}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-right whitespace-nowrap text-sm text-gray-700">{parseFloat(item.quantity).toFixed(2)}</td>
                                    <td className="px-6 py-4 text-right whitespace-nowrap text-sm text-gray-700">{formatCurrency(item.unit_price)}</td>
                                    <td className="px-6 py-4 text-right whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(parseFloat(item.quantity) * parseFloat(item.unit_price))}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50">
                                <tr className="border-t">
                                  <td colSpan="4" className="text-right px-6 py-2 text-sm text-gray-700">Subtotal</td>
                                  <td className="text-right px-6 py-2 text-sm font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.subtotal || 0)}</td>
                                </tr>
                                <tr>
                                  <td colSpan="4" className="text-right px-6 py-2 text-sm text-gray-700">Tax</td>
                                  <td className="text-right px-6 py-2 text-sm font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.tax_amount || 0)}</td>
                                </tr>
                                <tr>
                                  <td colSpan="4" className="text-right px-6 py-2 text-sm text-gray-700">Total</td>
                                  <td className="text-right px-6 py-2 text-sm font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.total_amount || 0)}</td>
                                </tr>
                                <tr>
                                  <td colSpan="4" className="text-right px-6 py-2 text-sm text-gray-700">Paid</td>
                                  <td className="text-right px-6 py-2 text-sm font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.amount_paid || 0)}</td>
                                </tr>
                                <tr>
                                  <td colSpan="4" className="text-right px-6 py-2 text-sm font-semibold text-gray-900">Balance Due</td>
                                  <td className="text-right px-6 py-2 text-sm font-semibold text-gray-900">{formatCurrency(selectedInvoiceForDetails.balance_due || 0)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {detailsTab === 'journals' && (
                      <div className="mt-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
                          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 text-sm">
                            <span className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                              Paid (net): <strong className="font-semibold">{formatCurrency(selectedInvoiceForDetails.amount_paid || 0)}</strong>
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-md bg-rose-50 px-3 py-1.5 text-rose-800 ring-1 ring-inset ring-rose-200">
                              Refunded: <strong className="font-semibold">{formatCurrency(selectedInvoiceForDetails.amount_refunded || 0)}</strong>
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-blue-800 ring-1 ring-inset ring-blue-200">
                              Credits: <strong className="font-semibold">{formatCurrency(selectedInvoiceForDetails.amount_credited || 0)}</strong>
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-md bg-amber-50 px-3 py-1.5 text-amber-900 ring-1 ring-inset ring-amber-200">
                              Written Off: <strong className="font-semibold">{formatCurrency(selectedInvoiceForDetails.amount_written_off || 0)}</strong>
                            </span>
                            <span className="col-span-2 sm:col-span-1 inline-flex items-center gap-2 rounded-md bg-gray-50 px-3 py-1.5 text-gray-800 ring-1 ring-inset ring-gray-200">
                              Balance: <strong className="font-semibold">{formatCurrency(selectedInvoiceForDetails.balance_due || 0)}</strong>
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button className="px-3 py-1.5 rounded-md text-sm bg-green-600 text-white hover:bg-green-700" onClick={() => openRecordPaymentModal(selectedInvoiceForDetails)}>Payment</button>
                            <button className="px-3 py-1.5 rounded-md text-sm bg-amber-100 text-amber-800 hover:bg-amber-200" onClick={() => openWriteOffModal(selectedInvoiceForDetails)}>Write-Off</button>
                            <button className="px-3 py-1.5 rounded-md text-sm bg-blue-100 text-blue-800 hover:bg-blue-200" onClick={() => openCreditNoteModal(selectedInvoiceForDetails)}>Credit Note</button>
                            <button className="px-3 py-1.5 rounded-md text-sm bg-pink-100 text-pink-800 hover:bg-pink-200" onClick={() => openRefundModal(selectedInvoiceForDetails)}>Refund</button>
                          </div>
                        </div>
                        {(selectedInvoiceForDetails.journals && selectedInvoiceForDetails.journals.length > 0) ? (
                          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                          <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Notes</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Receipt</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedInvoiceForDetails.journals.map(j => (
                                <tr key={j.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(j.created_at).toLocaleString()}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getJournalBadgeClass(j.journal_type)}`}>
                                      {(j.journal_type || '').replaceAll('_',' ')}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">{formatCurrency(j.amount || 0)}</td>
                                  <td className="px-6 py-4 text-sm text-gray-700">{j.notes || ''}</td>
                                  <td className="px-6 py-4 text-right">
                                    {(j.journal_type || '').startsWith('PAYMENT_') ? (
                                      <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => handleDownloadJournalReceipt(j)}
                                        disabled={printingJournalId === j.id}
                                      >
                                        {printingJournalId === j.id ? 'Generating…' : 'Generate Receipt'}
                                      </button>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                          </div>
                        ) : (
                          <p className="text-center text-gray-500 mt-4">No journals recorded for this invoice.</p>
                        )}
                      </div>
                    )}

                    {detailsTab === 'collections' && (
                      <CollectionsTab
                        invoice={selectedInvoiceForDetails}
                        authAxios={authAxios}
                        refreshInvoice={async () => {
                          try {
                            const r = await authAxios.get(`/invoices/${selectedInvoiceForDetails.id}/`);
                            const updated = r.data;
                            updated.invoice_items = updated.invoice_items || [];
                            setSelectedInvoiceForDetails(updated);
                          } catch (e) {
                            console.error('Failed to refresh invoice after collections action', e);
                          }
                        }}
                        showToast={showToast}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700" onClick={() => { closeDetailsModal(); openEditInvoiceModal(selectedInvoiceForDetails); }}>Edit Invoice</button>
                <button type="button" onClick={closeDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
